// /////////////////////////////////////////////////////////////
// [ファイル] SalesProjectPhaseHistory.tsx
// [概要] 案件詳細のフェーズ変更履歴タイムライン表示
//        trn_sfa_phase_history を新しい順に一覧し、
//        変更時点の金額・担当者・失注理由を添えて表示する
// /////////////////////////////////////////////////////////////
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Typography, Chip, Paper } from '@mui/material';
import * as Icons from '../constants/icons';
import LoadingView from './LoadingView';
import SalesProjectPhaseChip from './SalesProjectPhaseChip';
import { ep } from '../api/endpoints';

interface PhaseHistoryRow {
  history_id: number;
  phase_before: string | null;
  phase_after: string;
  lost_reason_category: string | null;
  lost_reason_name: string;
  lost_reason_detail: string | null;
  amount: number | null;
  expected_order_year: number | null;
  expected_order_month: number | null;
  main_staff_name: string | null;
  changed_by: string | null;
  changed_by_name: string | null;
  changed_at: string;
}

interface Props {
  projectId: string;
}

export default function SalesProjectPhaseHistory({ projectId }: Props) {
  const [rows, setRows] = useState<PhaseHistoryRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchHistory = async () => {
      try {
        setLoading(true);
        const res = await axios.post(ep('get_sfa_phase_history'), { projectId });
        if (!cancelled && res.data.status === 'success') {
          setRows(res.data.rows || []);
        }
      } catch (err) {
        console.error('Error fetching phase history:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchHistory();
    return () => { cancelled = true; };
  }, [projectId]);

  if (loading) {
    return <LoadingView message="フェーズ履歴を読み込み中..." height="120px" size={30} />;
  }

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        フェーズ変更履歴はまだありません。（履歴の記録は 2026-08-21 以降のフェーズ変更が対象です）
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {rows.map((row) => {
        const changedDate = row.changed_at ? row.changed_at.replace('T', ' ').slice(0, 16) : '';
        const isLost = row.phase_after === '7.失注';
        return (
          <Paper
            key={row.history_id}
            variant="outlined"
            sx={{ p: 1.5, borderRadius: 1, borderLeft: '4px solid', borderLeftColor: isLost ? 'error.light' : 'primary.light' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {row.phase_before ? (
                <>
                  <SalesProjectPhaseChip phase={row.phase_before} />
                  <Icons.ArrowForward sx={{ fontSize: 16, color: 'grey.500' }} />
                </>
              ) : (
                <Chip label="新規登録" size="small" sx={{ height: 22, fontSize: '0.75rem' }} />
              )}
              <SalesProjectPhaseChip phase={row.phase_after} />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {changedDate}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.75, flexWrap: 'wrap' }}>
              {row.amount != null && (
                <Typography variant="caption" color="text.secondary">
                  金額（当時）: {Number(row.amount).toLocaleString()} 円
                </Typography>
              )}
              {(row.expected_order_year || row.expected_order_month) && (
                <Typography variant="caption" color="text.secondary">
                  受注予定（当時）: {row.expected_order_year ? `${row.expected_order_year}年` : ''}{row.expected_order_month ? `${row.expected_order_month}月` : ''}
                </Typography>
              )}
              {row.main_staff_name && (
                <Typography variant="caption" color="text.secondary">
                  担当（当時）: {row.main_staff_name}
                </Typography>
              )}
              {(row.changed_by_name || row.changed_by) && (
                <Typography variant="caption" color="text.secondary">
                  操作者: {(row.changed_by_name || '').trim() || row.changed_by}
                </Typography>
              )}
            </Box>
            {isLost && (row.lost_reason_name || row.lost_reason_detail) && (
              <Box sx={{ mt: 0.75, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'bold', display: 'block' }}>
                  失注理由: {row.lost_reason_name || '未分類'}
                </Typography>
                {row.lost_reason_detail && (
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {row.lost_reason_detail}
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        );
      })}
    </Box>
  );
}
